import {
  Button,
  Box,
  TableContainer,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  handleCreatePermissionsApi,
  handleDeletePermission,
  handleGetPermissionsApi,
  handleUpdatePermissionsApi,
} from "../../../../api/mosaic/roles";
import {
  PermissionRequestBody,
  PermissionsResponse,
} from "../../../../api/mosaic/interface";
import PermissionModal from "./PermissionModal";
import { editButtonSx, deleteButtonSx, addButtonSx } from "../../../shared/buttonStyles";
import { useConfirmDialog } from "../../../shared/ConfirmDialogProvider";
import PageCard from "../../../shared/PageCard";
import { useToast } from "../../../shared/Toast";
import SearchBar from "../../../shared/SearchBar";
import SortableTableCell from "../../../shared/SortableTableCell";
import { useSortable } from "../../../shared/useSortable";

interface IProps {
  isNewForm: boolean;
  setIsNewForm: Dispatch<SetStateAction<boolean>>;
  setIsLoadingData: Dispatch<SetStateAction<boolean>>;
  isLoadingData: boolean;
  selectedView: string;
  openAddModal?: boolean;
  onAddModalOpened?: () => void;
}

const defaultValue = {
  permissionId: 0,
  name: "",
};

const PermissionView: React.FC<IProps> = ({
  setIsNewForm,
  isNewForm,
  isLoadingData,
  setIsLoadingData,
  selectedView,
  openAddModal,
  onAddModalOpened,
}) => {
  const [openPermissionModal, setOpenPermissionModal] = useState(false);
  const [isSubmittingData, setIsSubmittingData] = useState(false);
  const [formData, setFormData] = useState<PermissionRequestBody>(defaultValue);
  const [permissionData, setPermissionData] = useState<PermissionsResponse[]>([]);
  const [search, setSearch] = useState("");
  const { showSuccess, showError } = useToast();
  const { confirm } = useConfirmDialog();

  useEffect(() => {
    if (openAddModal) {
      setOpenPermissionModal(true);
      setIsNewForm(true);
      onAddModalOpened?.();
    }
  }, [openAddModal]);

  const handlePermissionModalClose = () => {
    setOpenPermissionModal(false);
    setFormData(defaultValue);
    setIsNewForm(false);
  };

  const handleDeletePerm = async (id: number) => {
    const confirmed = await confirm({ title: "Delete Permission", message: "Are you sure you want to delete this permission?", confirmText: "Delete" });
    if (!confirmed) return;
    try {
      await handleDeletePermission(id);
      setPermissionData(permissionData.filter((p) => p.id !== id));
      showSuccess("Permission deleted successfully");
    } catch (err) {
      showError("Error deleting permission");
      console.error(err);
    }
  };

  const handleUpdatePermission = async () => {
    try {
      if (formData.name === "") {
        showError("Please fill all fields");
        return;
      }
      const { permissionId, ...payload } = formData;
      setIsSubmittingData(true);
      await handleUpdatePermissionsApi(payload, permissionId as number);
      setFormData(defaultValue);
      setIsSubmittingData(false);
      handlePermissionModalClose();
      showSuccess("Permission updated successfully");
    } catch (err: unknown) {
      setIsSubmittingData(false);
      showError("Error updating permission");
    }
  };

  const handleOnCreatePermission = async () => {
    try {
      if (formData.name === "") {
        showError("Please fill all fields");
        return;
      }
      const { permissionId: _, ...payload } = formData;
      setIsSubmittingData(true);
      await handleCreatePermissionsApi(payload);
      setIsSubmittingData(false);
      setFormData(defaultValue);
      handlePermissionModalClose();
      showSuccess("Permission created successfully");
    } catch (err: unknown) {
      setIsSubmittingData(false);
      showError("Error creating permission");
    }
  };

  const handleOnPermissionChange = (key: string, value: string) => {
    setFormData({
      ...formData,
      [key]: key === "companyId" ? parseInt(value) : value,
    });
  };

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        setIsLoadingData(true);
        const response = await handleGetPermissionsApi();
        setPermissionData(response);
        setIsLoadingData(false);
      } catch (err) {
        setIsLoadingData(false);
        showError("Error fetching permissions");
        console.error(err);
      }
    };
    fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView, isSubmittingData]);

  const filteredPermissions = permissionData.filter((row) => {
    return row.name.toLowerCase().includes(search.toLowerCase());
  });

  const { sortedData, sortConfig, requestSort } = useSortable(filteredPermissions, "id", "desc");

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      <PageCard>
        {isLoadingData ? (
          <Box sx={{ textAlign: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <SearchBar value={search} onChange={setSearch} placeholder="Search permissions..." />
            <TableContainer sx={{ flex: 1, overflow: "auto" }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <SortableTableCell label="Permission" sortKey="name" active={sortConfig?.key === "name"} direction={sortConfig?.direction ?? "asc"} onClick={requestSort} />
                    <TableCell sx={{ backgroundColor: "#FAFAFA", fontWeight: 600 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedData.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 4, color: "#878787" }}>
                        No permissions found
                      </TableCell>
                    </TableRow>
                  )}
                  {sortedData.map((row, index) => (
                    <TableRow hover key={index}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                        <Button
                          variant="contained"
                          onClick={() => {
                            setOpenPermissionModal(true);
                            setIsNewForm(false);
                            setFormData({
                              name: row.name,
                              permissionId: row.id as number,
                            });
                          }}
                          sx={editButtonSx}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="contained"
                          onClick={() => handleDeletePerm(row.id)}
                          sx={deleteButtonSx}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </PageCard>

      {openPermissionModal && (
        <PermissionModal
          openModal={openPermissionModal}
          handleClose={handlePermissionModalClose}
          handleOnChange={handleOnPermissionChange}
          handleUpdatePermission={handleUpdatePermission}
          handleOnCreatePermission={handleOnCreatePermission}
          isNewForm={isNewForm}
          formData={formData}
          setFormData={setFormData}
          isLoading={isSubmittingData}
        />
      )}
    </Box>
  );
};

export default PermissionView;
